(function () {
  'use strict';

  var pageCache = new Map();
  var skuControllers = [];
  var DEBOUNCE_MS = 180;
  var SKU_CONTRACT_VERSION = '2';
  var SKU_RESET_PATH = '/collections/all-products';
  // Temporary local diagnostics. Set to false after manual storefront validation.
  var SKU_DEBUG = true;

  function debug(event, detail) {
    if (!SKU_DEBUG || !window.console || !console.debug) return;
    console.debug('[EcoFoot SKU] ' + event, detail === undefined ? '' : detail);
  }

  function normalizeSku(value) {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function requestJson(url, mode, signal) {
    debug(mode + ' request URL', url);
    return fetch(url, { credentials: 'same-origin', signal: signal })
      .then(function (response) {
        debug(mode + ' response status', response.status);
        return response.json().catch(function () {
          throw new Error('SKU search returned a non-JSON response.');
        }).then(function (body) {
          if (!response.ok) throw new Error(body.error || 'SKU search is temporarily unavailable.');
          if (
            !body ||
            body.kind !== 'ecofoot_sku_search' ||
            body.mode !== mode ||
            !Array.isArray(body.matches)
          ) {
            throw new Error('SKU search returned an unexpected response.');
          }
          var invalidMatch = body.matches.some(function (match) {
            return !match || typeof match.sku !== 'string' || typeof match.handle !== 'string';
          });
          if (invalidMatch) throw new Error('SKU search returned an invalid product match.');
          debug(mode + ' returned matches', {
            count: body.matches.length,
            handles: body.matches.map(function (match) { return match.handle; })
          });
          return body;
        });
      });
  }

  function skuUrl(query) {
    var url = new URL('/collections/all', window.location.origin);
    url.searchParams.set('ecofoot_sku', String(query || '').trim());
    url.hash = 'ecofoot-product-grid';
    return url;
  }

  function searchEndpoint(proxyUrl, query, mode) {
    var url = new URL(proxyUrl || '/apps/ecofoot-ai', window.location.origin);
    url.searchParams.set('intent', 'sku_search');
    url.searchParams.set('mode', mode);
    url.searchParams.set('q', String(query || '').slice(0, 100));
    url.searchParams.set('contract', SKU_CONTRACT_VERSION);
    return url.toString();
  }

  function isNativeSearchForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    try {
      return new URL(action || window.location.href, window.location.origin).pathname
        .replace(/\/+$/, '') === '/search';
    } catch (_error) {
      return false;
    }
  }

  function bindHeaderSearch() {
    if (window.__ecofootSkuHeaderSearchBound) return;
    window.__ecofootSkuHeaderSearchBound = true;
    document.querySelectorAll('form[data-ecofoot-catalog-search]').forEach(function (form) {
      if (form.dataset.ecofootCatalogSearchBound === 'true') return;
      form.dataset.ecofootCatalogSearchBound = 'true';
      var input = form.querySelector('input[name="q"], input[type="search"]');
      var suggestionsEnabled = form.dataset.ecofootCatalogSuggestions !== 'off';
      var results = form.querySelector('[data-ecofoot-header-search-results]');
      var status = form.querySelector('.predictive-search-status');
      if (!input || (suggestionsEnabled && !results)) return;
      var debounce = null;
      var request = null;
      var buttons = [];
      var activeIndex = -1;

      function close() {
        activeIndex = -1;
        buttons = [];
        if (results) {
          results.hidden = true;
          results.replaceChildren();
        }
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }

      function select(index) {
        if (!buttons.length) return;
        activeIndex = (index + buttons.length) % buttons.length;
        buttons.forEach(function (button, buttonIndex) {
          var active = buttonIndex === activeIndex;
          button.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        input.setAttribute('aria-activedescendant', buttons[activeIndex].id);
      }

      function render(matches) {
        if (!results) return;
        close();
        matches.forEach(function (match, index) {
          var button = document.createElement('button');
          button.type = 'button';
          button.id = 'ecofoot-header-option-' + index;
          button.className = 'predictive-search__item link link--text list-menu__item';
          button.dataset.ecofootHeaderSuggestion = match.sku;
          button.setAttribute('role', 'option');
          button.setAttribute('aria-selected', 'false');
          button.textContent = match.sku + (match.title ? ' — ' + match.title : '');
          results.appendChild(button);
        });
        buttons = Array.prototype.slice.call(
          results.querySelectorAll('[data-ecofoot-header-suggestion]')
        );
        if (buttons.length) {
          results.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        }
        if (status) {
          status.textContent = buttons.length
            ? buttons.length + ' EcoFoot catalogue suggestions available.'
            : 'No EcoFoot catalogue suggestions found.';
        }
      }

      function submit(query) {
        var value = String(query || '').trim();
        if (!value) return;
        window.location.assign(skuUrl(value).toString());
      }

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        submit(input.value);
      });
      form.addEventListener('reset', function () {
        window.setTimeout(close, 0);
      });
      input.addEventListener('input', function () {
        if (!suggestionsEnabled) return;
        window.clearTimeout(debounce);
        var query = input.value.trim();
        if (normalizeSku(query).length < 2) {
          close();
          return;
        }
        debounce = window.setTimeout(function () {
          if (request) request.abort();
          request = new AbortController();
          requestJson(
            searchEndpoint(form.dataset.proxyUrl, query, 'suggest'),
            'suggest',
            request.signal
          ).then(function (body) {
            render(body.matches);
          }).catch(function (error) {
            if (error.name !== 'AbortError') close();
          });
        }, DEBOUNCE_MS);
      });
      input.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown' && buttons.length) {
          event.preventDefault();
          select(activeIndex + 1);
        } else if (event.key === 'ArrowUp' && buttons.length) {
          event.preventDefault();
          select(activeIndex - 1);
        } else if (event.key === 'Enter' && activeIndex >= 0) {
          event.preventDefault();
          submit(buttons[activeIndex].dataset.ecofootHeaderSuggestion);
        } else if (event.key === 'Escape') {
          close();
        }
      });
      if (results) {
        results.addEventListener('click', function (event) {
          var suggestion = event.target.closest('[data-ecofoot-header-suggestion]');
          if (suggestion) submit(suggestion.dataset.ecofootHeaderSuggestion);
        });
      }
      document.addEventListener('click', function (event) {
        if (!form.contains(event.target)) close();
      });
    });

    // Safety fallback for any other native theme search form.
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!isNativeSearchForm(form) || form.dataset.ecofootCatalogSearchBound === 'true') return;
      var input = form.querySelector('input[name="q"], input[type="search"]');
      if (!input || !input.value.trim()) return;
      event.preventDefault();
      window.location.assign(skuUrl(input.value).toString());
    }, true);
  }

  function scrollToGrid(grid) {
    var sticky = document.querySelector('sticky-header, [data-sticky-header], .header-wrapper');
    var offset = sticky ? sticky.getBoundingClientRect().height : 0;
    var anchor = document.getElementById('ecofoot-product-grid') || grid;
    var top = anchor.getBoundingClientRect().top + window.scrollY - offset - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function sectionUrl(grid, page) {
    var url = new URL(window.location.href);
    url.searchParams.delete('ecofoot_ai');
    url.searchParams.delete('recommendation');
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', grid.dataset.sectionId);
    url.hash = '';
    return url.toString();
  }

  function sectionDocument(grid, page) {
    var url = sectionUrl(grid, page);
    if (!pageCache.has(url)) {
      debug('fetching collection page', { page: page, url: url });
      pageCache.set(url, fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('The product catalogue could not be loaded.');
          return response.text();
        })
        .then(function (html) {
          return new DOMParser().parseFromString(html, 'text/html');
        })
        .catch(function (error) {
          pageCache.delete(url);
          throw error;
        }));
    }
    return pageCache.get(url);
  }

  function itemForHandle(scope, handle) {
    var normalized = String(handle || '').trim().toLowerCase();
    return Array.prototype.find.call(
      scope.querySelectorAll('[data-ecofoot-product-item]'),
      function (item) {
        return String(item.dataset.ecofootProductHandle || '').trim().toLowerCase() === normalized;
      }
    );
  }

  function acquireItems(grid, matches) {
    var found = new Map();
    matches.forEach(function (match) {
      var item = itemForHandle(grid, match.handle);
      if (item) found.set(match.handle, item);
    });
    var page = 1;
    var pages = Number(grid.dataset.totalPages || 1);
    var currentPage = Number(grid.dataset.currentPage || 1);

    function nextPage() {
      if (found.size === matches.length || page > pages) return Promise.resolve(found);
      if (page === currentPage) {
        page += 1;
        return nextPage();
      }
      var fetchPage = page;
      page += 1;
      return sectionDocument(grid, fetchPage).then(function (documentPage) {
        matches.forEach(function (match) {
          if (found.has(match.handle)) return;
          var item = itemForHandle(documentPage, match.handle);
          if (item) found.set(match.handle, item.cloneNode(true));
        });
        debug('cards found after collection page', {
          page: fetchPage,
          found: Array.from(found.keys())
        });
        return nextPage();
      });
    }

    return nextPage();
  }

  function paginationNodes(grid) {
    return Array.prototype.slice.call(
      grid.querySelectorAll('.pagination-wrapper, nav.pagination')
    );
  }

  function clearRenderedResults(state) {
    var grid = state.grid;
    var list = state.list;
    grid.querySelectorAll('[data-ecofoot-sku-clone]').forEach(function (item) { item.remove(); });
    state.originalItems.forEach(function (item) {
      item.hidden = false;
      item.style.display = '';
      list.appendChild(item);
    });
    paginationNodes(grid).forEach(function (node) { node.hidden = false; });
    var empty = grid.querySelector('[data-ecofoot-sku-empty]');
    if (empty) empty.remove();
    list.hidden = false;
    grid.classList.remove('ecofoot-sku-grid--filtered', 'ecofoot-sku-grid--resolving');
  }

  function renderMatches(state, query, matches, correctedQuery) {
    clearRenderedResults(state);
    state.grid.classList.add('ecofoot-sku-grid--resolving');
    return acquireItems(state.grid, matches).then(function (found) {
      if (matches.length && found.size !== matches.length) {
        var missingHandles = matches.filter(function (match) {
          return !found.has(match.handle);
        }).map(function (match) { return match.handle; });
        throw new Error(
          'Matching product cards could not be loaded from the collection: ' +
          missingHandles.join(', ')
        );
      }
      var rendered = [];
      matches.forEach(function (match) {
        var item = found.get(match.handle);
        if (!item) return;
        if (!item.isConnected) {
          item.dataset.ecofootSkuClone = 'true';
          state.list.appendChild(item);
          debug('inserted native product card', item.dataset.ecofootProductHandle || '');
        }
        rendered.push(item);
      });

      Array.prototype.forEach.call(
        state.list.querySelectorAll('[data-ecofoot-product-item]'),
        function (item) {
          var visible = rendered.indexOf(item) !== -1;
          item.hidden = !visible;
          item.style.display = visible ? '' : 'none';
        }
      );
      rendered.forEach(function (item) { state.list.appendChild(item); });
      paginationNodes(state.grid).forEach(function (node) { node.hidden = true; });
      state.grid.classList.remove('ecofoot-sku-grid--resolving');
      state.grid.classList.add('ecofoot-sku-grid--filtered');
      state.setStatus(rendered.length
        ? correctedQuery && normalizeSku(correctedQuery) !== normalizeSku(query)
          ? 'Showing results for “' + correctedQuery + '” from “' + query + '”.'
          : rendered.length + ' matching product' + (rendered.length === 1 ? '' : 's') + ' shown.'
        : 'No safe catalogue matches found for “' + query + '”.');
      debug('final visible card count', rendered.length);
      scrollToGrid(state.grid);
    });
  }

  function initSkuFilter(root) {
    if (!root || root.dataset.ecofootSkuFilterBound === 'true') return;
    var grid = root.closest('[data-ecofoot-ai-grid]');
    var list = grid && grid.querySelector('#product-grid');
    var input = root.querySelector('[data-ecofoot-sku-input]');
    var apply = root.querySelector('[data-ecofoot-sku-apply]');
    var suggestions = root.querySelector('[data-ecofoot-sku-suggestions]');
    var suggestionsInner = root.querySelector('[data-ecofoot-sku-suggestions-inner]');
    var status = root.querySelector('[data-ecofoot-sku-status]');
    if (!grid || !list || !input || !apply || !suggestions || !suggestionsInner) return;

    root.dataset.ecofootSkuFilterBound = 'true';
    debug('controller initialized', {
      sectionId: grid.dataset.sectionId || '',
      totalPages: Number(grid.dataset.totalPages || 1)
    });
    var state = {
      root: root,
      grid: grid,
      list: list,
      input: input,
      originalItems: Array.prototype.slice.call(list.querySelectorAll(':scope > [data-ecofoot-product-item]')),
      activeIndex: -1,
      suggestionButtons: [],
      debounce: null,
      request: null,
      finalRequest: null,
      setStatus: function (message) {
        if (!status) return;
        status.textContent = message || '';
        status.classList.toggle('is-visible', Boolean(message));
      }
    };

    function endpoint(query, mode) {
      return searchEndpoint(root.dataset.proxyUrl, query, mode);
    }

    function closeSuggestions() {
      state.activeIndex = -1;
      state.suggestionButtons = [];
      suggestions.classList.remove('is-open');
      suggestionsInner.replaceChildren();
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }

    function setActiveSuggestion(index) {
      if (!state.suggestionButtons.length) return;
      state.activeIndex = (index + state.suggestionButtons.length) % state.suggestionButtons.length;
      state.suggestionButtons.forEach(function (button, buttonIndex) {
        var active = buttonIndex === state.activeIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      input.setAttribute('aria-activedescendant', state.suggestionButtons[state.activeIndex].id);
    }

    function renderSuggestions(matches) {
      closeSuggestions();
      matches.forEach(function (match, index) {
        var button = document.createElement('button');
        button.type = 'button';
        button.id = 'ecofoot-sku-option-' + index + '-' + Math.random().toString(36).slice(2);
        button.className = root.dataset.suggestionClass || '';
        button.dataset.ecofootSkuSuggestion = match.sku;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');
        var label = document.createElement('span');
        label.className = root.dataset.suggestionTitleClass || '';
        label.textContent = match.sku + (match.title ? ' — ' + match.title : '');
        var action = document.createElement('span');
        action.className = root.dataset.suggestionActionClass || '';
        action.textContent = 'Filter';
        button.appendChild(label);
        button.appendChild(action);
        suggestionsInner.appendChild(button);
      });
      state.suggestionButtons = Array.prototype.slice.call(
        suggestionsInner.querySelectorAll('[data-ecofoot-sku-suggestion]')
      );
      if (state.suggestionButtons.length) {
        suggestions.classList.add('is-open');
        input.setAttribute('aria-expanded', 'true');
      }
    }

    function loadSuggestions() {
      var query = input.value.trim();
      if (normalizeSku(query).length < 2) {
        closeSuggestions();
        return;
      }
      if (state.request) state.request.abort();
      state.request = new AbortController();
      requestJson(endpoint(query, 'suggest'), 'suggest', state.request.signal)
        .then(function (body) {
          state.setStatus('');
          renderSuggestions(body.matches);
        })
        .catch(function (error) {
          if (error.name !== 'AbortError') {
            debug('suggestion error', error.message);
            closeSuggestions();
            state.setStatus(error.message || 'SKU search is temporarily unavailable.');
          }
        });
    }

    function updateUrl(query, replace) {
      var target = skuUrl(query);
      window.history[replace ? 'replaceState' : 'pushState'](
        { ecofootSku: query },
        '',
        target.pathname + target.search + target.hash
      );
    }

    function applyQuery(query, options) {
      var value = String(query || '').trim().slice(0, 100);
      options = options || {};
      if (!value) {
        clearFilter(options);
        return Promise.resolve();
      }
      if (window.location.pathname.replace(/\/+$/, '') !== '/collections/all') {
        window.location.assign(skuUrl(value).toString());
        return Promise.resolve();
      }
      var currentParams = new URLSearchParams(window.location.search);
      var hasCollectionConstraint = Array.from(currentParams.keys()).some(
        function (key) {
          return key !== 'ecofoot_sku' &&
            key !== 'ecofoot_ai' &&
            key !== 'recommendation';
        }
      );
      if (hasCollectionConstraint) {
        window.location.assign(skuUrl(value).toString());
        return Promise.resolve();
      }
      closeSuggestions();
      if (state.request) state.request.abort();
      if (state.finalRequest) state.finalRequest.abort();
      var finalRequest = new AbortController();
      state.finalRequest = finalRequest;
      input.value = value;
      state.grid.classList.add('ecofoot-sku-grid--resolving');
      state.setStatus('Searching the full EcoFoot catalogue…');
      if (!options.fromUrl) {
        window.dispatchEvent(new CustomEvent('ecofoot:recommendation-clear'));
        updateUrl(value, false);
      }
      return requestJson(endpoint(value, 'all'), 'all', finalRequest.signal)
        .then(function (body) {
          return renderMatches(state, value, body.matches, body.correctedQuery);
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          debug('final search error', error.message);
          clearRenderedResults(state);
          state.setStatus(error.message || 'SKU search is temporarily unavailable.');
        })
        .finally(function () {
          if (state.finalRequest === finalRequest) state.finalRequest = null;
        });
    }

    function clearFilter(options) {
      options = options || {};
      if (state.request) state.request.abort();
      if (state.finalRequest) state.finalRequest.abort();
      closeSuggestions();
      clearRenderedResults(state);
      input.value = '';
      state.setStatus('');
      if (!options.fromUrl) {
        var url = new URL(window.location.href);
        if (!url.searchParams.has('ecofoot_sku')) return;
        url.pathname = SKU_RESET_PATH;
        url.searchParams.delete('ecofoot_sku');
        url.searchParams.delete('page');
        if (url.hash === '#ecofoot-product-grid') url.hash = '';
        window.history.pushState({}, '', url.pathname + url.search + url.hash);
      }
    }
    state.clearFilter = clearFilter;
    skuControllers.push(state);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    if (!suggestionsInner.id) {
      suggestionsInner.id = 'ecofoot-sku-listbox-' + Math.random().toString(36).slice(2);
    }
    input.setAttribute('aria-controls', suggestionsInner.id);
    suggestionsInner.setAttribute('role', 'listbox');

    input.addEventListener('input', function () {
      window.clearTimeout(state.debounce);
      if (!input.value.trim()) clearFilter();
      else state.debounce = window.setTimeout(loadSuggestions, DEBOUNCE_MS);
    });
    input.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' && state.suggestionButtons.length) {
        event.preventDefault();
        setActiveSuggestion(state.activeIndex + 1);
      } else if (event.key === 'ArrowUp' && state.suggestionButtons.length) {
        event.preventDefault();
        setActiveSuggestion(state.activeIndex - 1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        var selected = state.suggestionButtons[
          state.activeIndex >= 0 ? state.activeIndex : 0
        ];
        applyQuery(selected ? selected.dataset.ecofootSkuSuggestion : input.value);
      } else if (event.key === 'Escape') {
        closeSuggestions();
      }
    });
    apply.addEventListener('click', function () { applyQuery(input.value); });
    suggestionsInner.addEventListener('click', function (event) {
      var suggestion = event.target.closest('[data-ecofoot-sku-suggestion]');
      if (suggestion) applyQuery(suggestion.dataset.ecofootSkuSuggestion || '');
    });
    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) closeSuggestions();
    });
    window.addEventListener('popstate', function () {
      var params = new URLSearchParams(window.location.search);
      var query = params.get('ecofoot_sku') || '';
      if (query) applyQuery(query, { fromUrl: true });
      else clearFilter({ fromUrl: true });
    });

    var params = new URLSearchParams(window.location.search);
    var initialQuery = params.get('ecofoot_sku') || '';
    var hasRecommendation = params.get('ecofoot_ai') === '1' && params.get('recommendation');
    if (initialQuery && !hasRecommendation) applyQuery(initialQuery, { fromUrl: true });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-ecofoot-sku-search-shell]').forEach(initSkuFilter);
  }

  bindHeaderSearch();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  window.addEventListener('ecofoot:sku-clear', function () {
    skuControllers.filter(function (state) {
      return state.root && state.root.isConnected;
    }).forEach(function (state) {
      if (state.clearFilter) state.clearFilter({ fromUrl: true });
    });
  });
})();
